/**
 * TalkGroupModal
 *
 * Centered "Add Talk group" / "Edit Talk group" modal matching the May 2026
 * Figma. Reuses the shared `.os-*` palette (see SystemPage / SiteSettingsModal)
 * for the modal shell and adds Talk-group-specific styles under `.tg-*`.
 *
 * Layout (Figma):
 *   ┌────────────────── Add Talk group ────────────────────┐
 *   │ General settings ◀     │  Talk group name* ⓘ          │
 *   │                        │  [ Talk group name        ]  │
 *   │                        │                              │
 *   │                        │  Talk group icon ⓘ           │
 *   │                        │  [ ⬆ Upload icon ]           │
 *   │                        │                              │
 *   │                        │  Site restrictions ⓘ         │
 *   │                        │  [ All sites | Specific … ]  │
 *   │                        │                              │
 *   │                        │  (when Specific)             │
 *   │                        │  Site Activation Mode ⓘ      │
 *   │                        │  [ At Checkout | Manual … ]  │
 *   │                        │                              │
 *   │                        │  (when Manual)               │
 *   │                        │  Sites ⓘ                     │
 *   │                        │  [ Wallmart-1, Wallmart-2 ▾ ]│
 *   │                        │  Active at 2 Sites           │
 *   │                        │             Cancel  [ Add ]  │
 *   └──────────────────────────────────────────────────────┘
 *
 * Backend mapping (current TalkGroupCreateRequest fields):
 *   name              ← Talk group name
 *   description       ← (unused in this UI)
 *   singleSite        ← derived: true when Site restrictions = "Specific sites"
 *   siteId            ← first selected siteId when singleSite + manual
 *
 * Extras kept only in form state today (icon upload, multi-site selection,
 * activation mode) — would need matching backend columns to persist them
 * fully. The modal still surfaces them so the UX matches the mock.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

/* -------------------------------------------------------------------------- */

const TABS = [
  { id: 'general', label: 'General settings', icon: 'fa-sliders-h' },
];

const SITE_RESTRICTIONS = [
  { id: 'all',      label: 'All sites' },
  { id: 'specific', label: 'Specific sites' },
];

const ACTIVATION_MODES = [
  { id: 'checkout', label: 'At Checkout' },
  { id: 'manual',   label: 'Manual Selection' },
];

const EMPTY = {
  name: '',
  iconFile: null,
  iconUrl: '',
  broadcast: false,
  siteRestriction:   'all',       // 'all' | 'specific'
  siteActivationMode: 'checkout', // 'checkout' | 'manual'
  siteIds: [],
};

/* ---------- Small helpers ------------------------------------------------- */

function Help({ tip }) {
  return (
    <span className="os-help" title={tip} aria-label={tip}>
      <i className="far fa-question-circle"></i>
    </span>
  );
}

/** Label with optional required asterisk and trailing help icon. */
function FieldLabel({ children, required, tip, strong }) {
  return (
    <label className={`os-label ${strong ? 'tg-label-strong' : ''}`}>
      <span>{children}{required && <span className="os-req">*</span>}</span>
      {tip && <Help tip={tip} />}
    </label>
  );
}

/** Two-pill segmented control. */
function Segmented({ options, value, onChange }) {
  return (
    <div className="tg-segmented" role="tablist">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          role="tab"
          aria-selected={value === opt.id}
          className={`tg-segmented-btn ${value === opt.id ? 'active' : ''}`}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sites multi-select — Figma "SELECT SITES FROM THE LIST" dropdown          */
/* -------------------------------------------------------------------------- */

function siteIdOf(s) { return s?.siteId ?? s?.id; }
function siteNameOf(s) { return s?.siteName || s?.name || `Site ${siteIdOf(s)}`; }

function SitesPicker({ sites, selected, onChange }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const masterRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sites.filter((s) => siteNameOf(s).toLowerCase().includes(q)) : sites;
  }, [sites, query]);

  const allIds = useMemo(() => sites.map(siteIdOf), [sites]);
  const allSelected  = sites.length > 0 && selected.length === sites.length;
  const someSelected = selected.length > 0 && !allSelected;

  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleAll = () => onChange(allSelected ? [] : allIds);

  const toggleOne = (id) =>
    onChange(selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id]);

  const summary = useMemo(() => {
    if (selected.length === 0) return '';
    const names = selected
      .map((id) => sites.find((s) => siteIdOf(s) === id))
      .filter(Boolean)
      .map(siteNameOf);
    return names.join(', ');
  }, [selected, sites]);

  return (
    <div className={`sites-mselect ${open ? 'open' : ''}`} ref={wrapRef}>
      <div
        role="button"
        tabIndex={0}
        className="sites-mselect-trigger tg-sites-trigger"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
      >
        <div className="sites-mselect-summary">
          {selected.length === 0
            ? <span className="sites-mselect-placeholder">Select sites…</span>
            : <span className="sites-mselect-summary-text">{summary}</span>}
        </div>
        <i className="fas fa-chevron-down sites-mselect-caret" aria-hidden="true" />
      </div>

      {open && (
        <div className="sites-mselect-menu tg-sites-menu" role="listbox">
          <div className="sites-mselect-caption">SELECT SITES FROM THE LIST</div>
          <div className="tg-sites-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              autoFocus
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {sites.length === 0 ? (
            <div className="sites-mselect-empty">No sites for this organization</div>
          ) : (
            <>
              <label className="sites-mselect-row sites-mselect-row-all">
                <input
                  ref={masterRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                />
                <span className="sites-mselect-box" aria-hidden="true">
                  {allSelected && <i className="fas fa-check" />}
                  {someSelected && !allSelected && <i className="fas fa-minus" />}
                </span>
                <span className="sites-mselect-label sites-mselect-label-strong">Select all sites</span>
              </label>

              {filtered.length === 0 && (
                <div className="sites-mselect-empty">No sites match "{query}"</div>
              )}

              {filtered.map((s) => {
                const id = siteIdOf(s);
                const checked = selected.includes(id);
                return (
                  <label key={id} className="sites-mselect-row">
                    <input type="checkbox" checked={checked} onChange={() => toggleOne(id)} />
                    <span className="sites-mselect-box" aria-hidden="true">
                      {checked && <i className="fas fa-check" />}
                    </span>
                    <span className="sites-mselect-label">
                      <span className="sites-mselect-name">{siteNameOf(s)}</span>
                    </span>
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Modal                                                                     */
/* -------------------------------------------------------------------------- */

export default function TalkGroupModal({
  open,
  initial,
  sites = [],
  onSave,
  onClose,
  saving = false,
}) {
  const [form, setForm]     = useState(EMPTY);
  const [active, setActive] = useState('general');
  const [showErrors, setShowErrors] = useState(false);
  const isEdit = !!initial;
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
    setActive('general');
    setShowErrors(false);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUploadClick = () => fileInputRef.current?.click();
  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setField('iconFile', file);
    setField('iconUrl', URL.createObjectURL(file));
  };

  const nameErr = showErrors && !form.name?.trim();

  const handleSubmit = () => {
    setShowErrors(true);
    if (!form.name?.trim()) return;
    onSave(form);
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit Talk group' : 'Add Talk group'}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="os-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>{isEdit ? 'Edit Talk group' : 'Add Talk group'}</h2>
        </header>

        <div className="os-modal-body">
          {/* Left rail tabs */}
          <aside className="os-side">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`os-side-item ${active === t.id ? 'active' : ''}`}
                onClick={() => setActive(t.id)}
              >
                <i className={`fas ${t.icon}`}></i>
                <span>{t.label}</span>
              </button>
            ))}
          </aside>

          {/* Right pane */}
          <section className="os-pane">
            {/* Talk group name */}
            <div className="os-field">
              <FieldLabel required tip="Display name for this talk group">
                Talk group name
              </FieldLabel>
              <input
                type="text"
                className={`os-input ${nameErr ? 'os-input-error' : ''}`}
                placeholder="Talk group name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
              />
              {nameErr && (
                <div className="os-field-error">
                  <i className="fas fa-info-circle"></i> This field is required
                </div>
              )}
            </div>

            {/* Broadcast toggle */}
            <div className="os-field">
              <div className="tg-broadcast-row">
                <FieldLabel tip="When enabled, the talk group is provisioned across all sites; membership is dynamically determined by the user's Checkout site.">
                  Broadcast
                </FieldLabel>
                <label className="tg-toggle">
                  <input
                    type="checkbox"
                    checked={form.broadcast}
                    onChange={(e) => setField('broadcast', e.target.checked)}
                  />
                  <span className="tg-toggle-slider" />
                </label>
              </div>
              {form.broadcast && (
                <div className="tg-broadcast-hint">
                  <i className="fas fa-info-circle"></i>{' '}
                  Provisioned across all sites; membership is dynamically determined by the user's Checkout site.
                </div>
              )}
            </div>

            {/* Talk group icon */}
            <div className="os-field">
              <FieldLabel strong tip="Optional icon shown next to the talk group across the portal">
                Talk group icon
              </FieldLabel>
              <div className="tg-upload-row">
                <button
                  type="button"
                  className="tg-upload-btn"
                  onClick={onUploadClick}
                >
                  {form.iconUrl ? (
                    <img src={form.iconUrl} alt="" className="tg-upload-preview" />
                  ) : (
                    <i className="fas fa-cloud-upload-alt"></i>
                  )}
                  <span>{form.iconUrl ? 'Change icon' : 'Upload icon'}</span>
                </button>
                {form.iconUrl && (
                  <button
                    type="button"
                    className="tg-upload-clear"
                    title="Remove icon"
                    onClick={() => { setField('iconFile', null); setField('iconUrl', ''); }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="tg-upload-input"
                  onChange={onFilePicked}
                />
              </div>
            </div>

            {/* Site restrictions */}
            <div className="os-field">
              <FieldLabel strong tip="Choose whether the talk group is available across all sites or only specific ones.">
                Site restrictions
              </FieldLabel>
              <Segmented
                options={SITE_RESTRICTIONS}
                value={form.siteRestriction}
                onChange={(v) => setField('siteRestriction', v)}
              />
            </div>

            {/* Activation mode — shown only when 'specific' */}
            {form.siteRestriction === 'specific' && (
              <div className="os-field">
                <FieldLabel strong tip="At Checkout: the talk group is activated automatically based on the user's checkout site. Manual Selection: you pick the sites where it's active.">
                  Site Activation Mode
                </FieldLabel>
                <Segmented
                  options={ACTIVATION_MODES}
                  value={form.siteActivationMode}
                  onChange={(v) => setField('siteActivationMode', v)}
                />
              </div>
            )}

            {/* Sites picker — shown only on Specific + Manual */}
            {form.siteRestriction === 'specific'
              && form.siteActivationMode === 'manual' && (
              <div className="os-field">
                <FieldLabel tip="Sites where this talk group is active.">Sites</FieldLabel>
                <SitesPicker
                  sites={sites}
                  selected={form.siteIds}
                  onChange={(ids) => setField('siteIds', ids)}
                />
                <div className="tg-active-hint">
                  Active at {form.siteIds.length} Site{form.siteIds.length === 1 ? '' : 's'}
                </div>
              </div>
            )}
          </section>
        </div>

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Add')}
          </button>
        </footer>
      </div>
    </div>
  );
}

